import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Field } from "formik";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../Style/CompanySettings.css";
import { path_url } from "../../Config/config";
import countryData from "../../Utils/countryData.json";

const customerSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
});

// Blank starting point for a brand-new record. No localStorage fallback here —
// a fresh Company Settings form should always start empty.
const emptyValues = {
  name: "",
  address: "",
  address2: "",
  city: "",
  postCode: "",
  region: "",
  contactTo: "",
  companyStatus: "",
  phone: "",
  vatNo: "",
  gln: "",
  useGln: false,
  notes: "",
  invoiceFormat: "",
  invoiceDueDayPeriod: "",
  taskDueDayPeriod: "",
  serviceRequestDueDayPeriod: "",
  interactionRecordDueDayPeriod: "",
  purchaseOrderDueDayPeriod: "",
  quotationDueDayPeriod: "",
  currency: "",
};

function FormLabel({ text, required }) {
  return (
    <label className="cs-label">
      {text}
      {required && <sup>*</sup>}
    </label>
  );
}

export default function General() {
  // null = "still checking whether saved data exists"
  // emptyValues = "checked, nothing saved yet"
  // {...} = "checked, here is the real saved record"
  const [dbValues, setDbValues] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const adminId = localStorage.getItem("adminId");
        if (!token || !adminId) {
          setDbValues(emptyValues);
          setLoading(false);
          return;
        }
        const authHeader = { Authorization: `Bearer ${token}` };

        const [companyRes, defaultsRes] = await Promise.allSettled([
          axios.get(`${path_url}/company/${adminId}`, { headers: authHeader }),
          axios.get(`${path_url}/admindefaultvalues/${adminId}`, {
            headers: authHeader,
          }),
        ]);

        const org =
          companyRes.status === "fulfilled"
            ? companyRes.value?.data?.body || companyRes.value?.data || {}
            : {};
        const defaults =
          defaultsRes.status === "fulfilled"
            ? defaultsRes.value?.data?.body || defaultsRes.value?.data || {}
            : {};

        // Only treat this as "existing saved data" if the backend actually
        // returned a real company record (not just an empty/failed response).
        const hasExistingCompany = Boolean(org && org.companyName);

        if (!hasExistingCompany) {
          // Genuinely nothing saved yet for this admin — keep the form blank.
          setDbValues(emptyValues);
          setLoading(false);
          return;
        }

        const loaded = {
          name: org.companyName || "",
          address: org.address || "",
          address2: "",
          city: org.state || "",
          postCode: org.pincode || "",
          region: org.country || "",
          contactTo: org.email || "",
          companyStatus: "",
          phone: org.phoneNumber || "",
          // Bug fix: this used to fall back to org.email, which silently
          // put the company email into the VAT field. VAT has no relation
          // to email, so only use a real VAT value if the backend sends one.
          vatNo: org.vatNo || "",
          gln: "",
          useGln: false,
          notes: "",
          invoiceFormat: defaults.invoiceFormat || "",
          invoiceDueDayPeriod: defaults.invoiceDueDayPeriod || "",
          taskDueDayPeriod: defaults.taskDueDayPeriod || "",
          serviceRequestDueDayPeriod:
            defaults.serviceRequestDueDayPeriod || "",
          interactionRecordDueDayPeriod:
            defaults.interactionRecordDueDayPeriod || "",
          purchaseOrderDueDayPeriod: defaults.purchaseOrderDueDayPeriod || "",
          quotationDueDayPeriod: defaults.quotationDueDayPeriod || "",
          currency: org.currencies || defaults.currency || "",
        };

        setDbValues(loaded);
      } catch (err) {
        console.warn(
          "Could not load company settings from DB:",
          err?.response?.data || err.message,
        );
        // On error, still resolve to blank rather than leaving the form
        // stuck or falling back to stale local data.
        setDbValues(emptyValues);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanySettings();
  }, []);

  // Avoid rendering the form with a transient/incorrect state while the
  // real answer (existing record vs. blank) is still being determined.
  if (loading || dbValues === null) {
    return (
      <div className="cs-section-header">
        <p>Loading company settings…</p>
      </div>
    );
  }

  return (
    <>
      <Formik
        initialValues={dbValues}
        enableReinitialize={true}
        validationSchema={customerSchema}
        onSubmit={async (values) => {
          try {
            const token = localStorage.getItem("accessToken");
            const adminId = localStorage.getItem("adminId");

            if (!token || !adminId) {
              toast.error("Authentication missing. Please log in again.");
              return;
            }

            const authHeader = {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            };

            const companyPayload = {
              owneruserid: adminId,
              usercid: adminId,
              useradminid: adminId,
              companyName: values.name,
              address: values.address,
              state: values.city,
              pincode: values.postCode,
              country: values.region,
              phoneNumber: values.phone,
              email: values.contactTo,
              vatNo: values.vatNo,
              currencies: values.currency || null,
              active: false,
            };

            const defaultsPayload = {
              useradminid: adminId,
              usercid: adminId,
              invoiceFormat: values.invoiceFormat,
              invoiceDueDayPeriod: values.invoiceDueDayPeriod,
              taskDueDayPeriod: values.taskDueDayPeriod,
              serviceRequestDueDayPeriod: values.serviceRequestDueDayPeriod,
              interactionRecordDueDayPeriod:
                values.interactionRecordDueDayPeriod,
              purchaseOrderDueDayPeriod: values.purchaseOrderDueDayPeriod,
              quotationDueDayPeriod: values.quotationDueDayPeriod,
              currency: values.currency,
            };

            const [companyRes, defaultsRes] = await Promise.allSettled([
              axios.post(`${path_url}/company`, companyPayload, {
                headers: authHeader,
              }),
              axios.post(
                `${path_url}/admindefaultvalues/admin/defaultvalue`,
                defaultsPayload,
                { headers: authHeader },
              ),
            ]);

            const companyOk =
              companyRes.status === "fulfilled" &&
              (companyRes.value?.status === 200 ||
                companyRes.value?.data?.statusCodeValue === 200);
            const defaultsOk =
              defaultsRes.status === "fulfilled" &&
              (defaultsRes.value?.status === 200 ||
                defaultsRes.value?.data?.statusCodeValue === 200);

            if (companyOk || defaultsOk) {
              // Update our own in-memory copy so the form reflects what was
              // just saved, without relying on localStorage as a data source.
              setDbValues(values);
              toast.success("Company settings saved to database successfully!");
            } else {
              const errMsg =
                companyRes.reason?.response?.data ||
                defaultsRes.reason?.response?.data ||
                "Save failed";
              toast.error(
                "Failed to save: " +
                  (typeof errMsg === "string"
                    ? errMsg
                    : JSON.stringify(errMsg)),
              );
            }
          } catch (error) {
            console.error("Save company settings error:", error);
            toast.error(
              "Failed to save: " +
                (error?.response?.data?.message ||
                  error?.response?.data ||
                  error.message),
            );
          }
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <fieldset>
              <div className="cs-section-header">
                <p>General</p>
              </div>

              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="cs-form-row">
                    <FormLabel text="Name" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Name"
                        name="name"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <small className="cs-error">{formik.errors.name}</small>
                      )}
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Address" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Address"
                        name="address"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.address}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Address2" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Address2"
                        name="address2"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.address2}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="City" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="City"
                        name="city"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.city}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Post/code" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Post/code"
                        name="postCode"
                        value={formik.values.postCode}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Region" />
                    <div className="cs-field-wrap">
                      <Field
                        as="select"
                        name="region"
                        id="region"
                        className="cs-select"
                      >
                        <option value="">Select Region</option>
                        {countryData.countries.map((country, i) => (
                          <option key={i} value={country.country_name}>
                            {country.country_name}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Contact to" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Contact to"
                        name="contactTo"
                        value={formik.values.contactTo}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Company Status" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Company Status"
                        name="companyStatus"
                        value={formik.values.companyStatus}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="cs-form-row">
                    <FormLabel text="Phone No." />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="Phone No."
                        name="phone"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="VAT Registration No." />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="VAT Registration No."
                        name="vatNo"
                        value={formik.values.vatNo}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="GLN" />
                    <div className="cs-field-wrap">
                      <input
                        className="cs-input"
                        type="text"
                        placeholder="GLN"
                        name="gln"
                        value={formik.values.gln}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Use GLN in Electronics" />
                    <div className="cs-field-wrap cs-switch-wrapper">
                      <div className="custom-control custom-switch">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="customSwitches"
                          name="useGln"
                          checked={formik.values.useGln}
                          onChange={formik.handleChange}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="customSwitches"
                        ></label>
                      </div>
                    </div>
                  </div>

                  <div className="cs-form-row">
                    <FormLabel text="Notes" />
                    <div className="cs-field-wrap">
                      <textarea
                        className="cs-textarea"
                        id="notes"
                        name="notes"
                        rows={3}
                        placeholder="Notes"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.notes}
                      />
                    </div>
                  </div>

                  <div className="cs-btn-row">
                    <button
                      type="button"
                      className="cs-btn-cancel"
                      onClick={() => formik.resetForm()}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="cs-btn-save">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
        )}
      </Formik>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}